export interface HonchConfig {
    /** Your Honch project API key (required) */
    token: string;

    /** API host for event capture */
    api_host: string;

    /** Enable automatic pageview tracking */
    autocapture: boolean;

    /** Enable automatic click tracking */
    capture_clicks: boolean;

    /** Enable automatic form submission tracking */
    capture_forms: boolean;

    /** Enable session recording */
    session_recording: boolean;

    /** Session recording sample rate (0.0 - 1.0) */
    session_recording_sample_rate: number;

    /** Enable gzip compression for recording payloads (reduces size ~85%) */
    session_recording_compression: boolean;

    /** Enable Web Vitals tracking */
    capture_vitals: boolean;

    /** Batch size before flushing events */
    batch_size: number;

    /** Flush interval in milliseconds */
    flush_interval_ms: number;

    /** Maximum retry attempts for failed requests */
    max_retries: number;

    /** Enable debug logging */
    debug: boolean;

    /** Disable all tracking (useful for development) */
    disabled: boolean;

    /** Properties to add to every event */
    default_properties: Record<string, unknown>;

    /** URLs to exclude from tracking (regex patterns) */
    exclude_urls: RegExp[];

    /** CSS selectors to exclude from autocapture */
    exclude_elements: string[];

    /** Persist user identity across sessions */
    persistence: 'localStorage' | 'sessionStorage' | 'memory';

    /** Custom storage prefix */
    storage_prefix: string;

    /** Respect Do Not Track browser setting */
    respect_dnt: boolean;

    /** Callback when SDK is ready */
    loaded?: (honch: HonchInstance) => void;

    /** Custom fetch implementation */
    fetch_fn?: typeof fetch;
}

export interface HonchInstance {
    capture: (event: string, properties?: Record<string, unknown>) => void;
    identify: (distinctId: string, properties?: Record<string, unknown>) => void;
    alias: (alias: string, distinctId?: string) => void;
    reset: () => void;
    setPersonProperties: (properties: Record<string, unknown>) => void;
    setPersonPropertiesOnce: (properties: Record<string, unknown>) => void;
    register: (properties: Record<string, unknown>) => void;
    unregister: (key: string) => void;
    getDistinctId: () => string;
    getSessionId: () => string;
    flush: () => Promise<void>;
    opt_out: () => void;
    opt_in: () => void;
    isOptedOut: () => boolean;
    debug: (enabled?: boolean) => void;
}

export const DEFAULT_CONFIG: Omit<HonchConfig, 'token'> = {
    api_host: 'https://capture.honch.io',
    autocapture: true,
    capture_clicks: true,
    capture_forms: true,
    session_recording: false,
    session_recording_sample_rate: 1.0,
    session_recording_compression: true,
    capture_vitals: true,
    batch_size: 10,
    flush_interval_ms: 5000,
    max_retries: 3,
    debug: false,
    disabled: false,
    default_properties: {},
    exclude_urls: [],
    exclude_elements: ['[data-honch-ignore]', '.honch-ignore'],
    persistence: 'localStorage',
    storage_prefix: 'honch_',
    respect_dnt: true,
};
